import { Injectable, Signal, WritableSignal, effect, inject, signal } from '@angular/core';
import { DB_CONNECTION_CONFIGS } from '../tokens';
import { DbConnectionConfig } from './db-connection-provider.interface';
import { PouchDB } from './pouchdb-init';
import { SplashScreenService } from '../../services/splash-screen.service';
import { SyncMode, LiveSyncStatus } from '../types/sync-mode.types';

/**
 * Abstrakte Basisklasse für PouchDB-basierte Services.
 * Kapselt gemeinsame DB-Verbindungs- und Initialisierungslogik.
 *
 * Subklassen müssen implementieren:
 * - remoteDbName, localDbName: Namen der Datenbanken
 * - projectSignal, userOfflineModeSignal, networkOnlineSignal: Reaktive Abhängigkeiten
 * - onInitializeRemoteDatabase, onInitializeLocalDatabase: App-spezifische Init-Logik
 * - onDatabasesInitialized, onDatabasesClosed: Lifecycle-Hooks
 * - shouldKeepEmptyLocalDb: Entscheidungslogik für leere lokale DBs
 */
@Injectable()
export abstract class BasePouchDbService {
	// Dependencies
	protected dbConnections = inject(DB_CONNECTION_CONFIGS);
	protected splashScreen = inject(SplashScreenService);

	// DB Instances
	protected localDb?: PouchDB.Database | null;
	protected remoteDb?: PouchDB.Database | null;

	// Status
	protected isInitialized = false;

	// Live-Sync
	private _liveSyncHandle: any | null = null;
	liveSyncStatus: WritableSignal<LiveSyncStatus> = signal<LiveSyncStatus>('idle');

	/**
	 * Synchronisationsmodus. Default: 'checkout-checkin' (bestehende Logik).
	 * Subklassen überschreiben zu 'live-sync' für kontinuierliche bidirektionale Replikation.
	 */
	protected get syncMode(): SyncMode { return 'checkout-checkin'; }

	/**
	 * Kurzform-Prüfung für Live-Sync-Modus.
	 */
	protected isLiveSync(): boolean { return this.syncMode === 'live-sync'; }

	// Abstract Properties: DB-Namen
	abstract get remoteDbName(): string;
	abstract get localDbName(): string;

	// Abstract Signals: Reaktive Dependencies für Effects
	// DataService: echte Signals von ProjectService und OfflineModeService
	// ProjectService: kann konstante Signals zurückgeben falls keine Effects benötigt
	protected abstract get projectSignal(): Signal<string | null>;
	protected abstract get userOfflineModeSignal(): Signal<boolean>;
	protected abstract get networkOnlineSignal(): Signal<boolean>;

	// Abstract Hooks: App-spezifische Initialisierung
	/**
	 * Wird nach erfolgreicher Remote-DB-Initialisierung aufgerufen.
	 * Z.B. für ensureApplicationDesignDocument, Migrationen, etc.
	 */
	protected abstract onInitializeRemoteDatabase(db: PouchDB.Database): Promise<void>;

	/**
	 * Wird nach erfolgreicher Lokal-DB-Initialisierung aufgerufen.
	 * Z.B. für ensureApplicationDesignDocument.
	 */
	protected abstract onInitializeLocalDatabase(db: PouchDB.Database): Promise<void>;

	/**
	 * Wird aufgerufen wenn beide DBs (remote und lokal) initialisiert sind.
	 * Z.B. für Events emittieren.
	 */
	protected abstract onDatabasesInitialized(): void;

	/**
	 * Wird aufgerufen wenn DBs geschlossen werden.
	 */
	protected abstract onDatabasesClosed(): void;

	/**
	 * Entscheidet ob eine leere lokale DB behalten werden soll.
	 * DataService: prüft pointsDownloaded und userOfflineMode
	 * ProjectService: kann immer false zurückgeben (leere DBs verwerfen)
	 */
	protected abstract shouldKeepEmptyLocalDb(dbName: string): Promise<boolean>;

	/**
	 * Hook für Splash-Screen Updates während Initialisierung.
	 * Nutzt den SplashScreenService der Library.
	 */
	protected updateSplashStatus(text: string): void {
		this.splashScreen.updateStatus(text);
	}

	/**
	 * Optional: Hook für Connection Failed Events
	 */
	protected onConnectionFailed(): void {
		// Subklassen können überschreiben
	}

	/**
	 * Optional: Hook für Connection Success Events
	 */
	protected onConnectionSuccess(message?: string): void {
		// Subklassen können überschreiben
	}

	/**
	 * Testet DB-Verbindung via db.info()
	 */
	protected _tryDB(db: PouchDB.Database, success: () => void, failure: (err: any) => void): void {
		try {
			db.info().then((info: PouchDB.Core.DatabaseInfo) => {
				const inf = info as any;
				const err = inf.error;
				const local_and_no_doc = inf.adapter === "idb" && info.doc_count === 0;
				if (err || local_and_no_doc) {
					return failure(err);
				} else {
					return success();
				}
			}).catch((err: any) => {
				return failure(err);
			});
		}
		catch (err: any) {
			failure(err);
		}
	}

	/**
	 * Iteriert durch dbConnections Array und testet jede Verbindung
	 */
	protected _tryDBIterate(
		dbName: string,
		success: (db: PouchDB.Database) => void,
		failure: () => void,
		i = 0
	): void {
		if (i === this.dbConnections.length) {
			return failure();
		}
		const conn = this.dbConnections[i];
		const dbUrl = conn.url(dbName);
		const db = new PouchDB(dbUrl, { skip_setup: !conn.autoCreate });
		this._tryDB(db, () => {
			return success(db);
		}, () => {
			this._tryDBIterate(dbName, success, failure, i + 1);
		});
	}

	/**
	 * Stellt sicher, dass remoteDb verfügbar ist.
	 * Initialisiert die Verbindung on-demand falls nötig (z.B. für Sync aus dem Offline-Modus).
	 * @returns true wenn remoteDb verfügbar ist, false wenn keine Verbindung möglich
	 */
	async ensureRemoteDb(): Promise<boolean> {
		if (this.remoteDb) return true;
		if (!this.networkOnlineSignal()) return false;

		return new Promise<boolean>(resolve => {
			this._initRemoteDatabase(() => {
				resolve(!!this.remoteDb);
			});
		});
	}

	/**
	 * Initialisiert Remote-Datenbank.
	 * Ruft onInitializeRemoteDatabase Hook auf.
	 */
	protected _initRemoteDatabase(callback: () => void): void {
		const isConnected = this.networkOnlineSignal();

		if (!isConnected) {
			this.onConnectionFailed();
			this.remoteDb = null;
			callback();
		} else {
			this.updateSplashStatus('Mit Datenbank verbinden...');
			this._tryDBIterate(this.remoteDbName, (db: PouchDB.Database) => {
				this.updateSplashStatus('Datenbank initialisieren...');
				this.onInitializeRemoteDatabase(db).then(() => {
					this.remoteDb = db;
					callback();
				}).catch(() => {
					this.remoteDb = null;
					this.onConnectionFailed();
					callback();
				});
			}, () => {
				this.remoteDb = null;
				this.onConnectionFailed();
				callback();
			});
		}
	}

	/**
	 * Initialisiert lokale Datenbank.
	 * Beinhaltet Corruption-Handling und Entscheidungslogik für leere DBs.
	 * Ruft onInitializeLocalDatabase Hook auf.
	 */
	protected async _initLocalDatabase(dbName: string, callback: () => void): Promise<void> {
		this.updateSplashStatus('Lokale Datenbank laden...');
		try {
			const ldb = new PouchDB(dbName);
			const info = await ldb.info();
			const hasDocuments = info.doc_count > 0;

			if (hasDocuments) {
				try {
					await this.onInitializeLocalDatabase(ldb);
					this.localDb = ldb;
					callback();
				} catch {
					await ldb.destroy().catch(() => { });
					this.localDb = null;
					callback();
				}
			} else {
				const shouldKeep = await this.shouldKeepEmptyLocalDb(dbName);

				if (shouldKeep) {
					await this.onInitializeLocalDatabase(ldb);
					this.localDb = ldb;
					callback();
				} else {
					await ldb.close().catch(() => { });
					this.localDb = null;
					callback();
				}
			}
		} catch (err) {
			console.warn('[_initLocalDatabase] Fehler, lösche beschädigte DB:', err);
			try {
				const indexDbName = '_pouch_' + dbName;
				await new Promise<void>((resolve) => {
					const request = indexedDB.deleteDatabase(indexDbName);
					request.onsuccess = () => resolve();
					request.onerror = () => resolve();
					request.onblocked = () => resolve();
				});
			} catch {
				// Ignoriere Fehler beim Löschen
			}
			this.localDb = null;
			callback();
		}
	}

	/**
	 * Initialisiert beide Datenbanken und registriert Effects für:
	 * - Projektwechsel (localDb neu initialisieren)
	 * - Offline-Modus-Wechsel (currentDb aktualisieren)
	 * - Netzwerkänderungen (remoteDb neu initialisieren wenn verfügbar)
	 */
	protected _initDatabases(): void {
		// Live-Sync-Modus: eigene Initialisierungslogik
		if (this.isLiveSync()) {
			this._initDatabasesLiveSync();
			return;
		}

		// WICHTIG: Den Offline-Modus EINMAL zu Beginn festlegen und konsistent verwenden
		// Sonst kann sich der Wert zwischen Initialisierungsstart und Callback ändern
		const initInOfflineMode = this.userOfflineModeSignal();

		const maybeInitialized = (emit: boolean) => {
			// Im Offline-Modus (Benutzerpräferenz): Warte auf echte lokale DB (nicht null/undefined)
			// Im Online-Modus: remoteDb muss verfügbar sein, localDb kann null sein (localDb !== undefined)
			// WICHTIG: Nutze initInOfflineMode (fixiert beim Start), nicht userOfflineModeSignal() (kann sich ändern)
			const localDbReady = initInOfflineMode ? !!this.localDb : this.localDb !== undefined;
			const remoteDbReady = this.remoteDb !== undefined;

			if (remoteDbReady && localDbReady) {
				this.isInitialized = true;
				if (emit) {
					this.onDatabasesInitialized();
				}
			}
		};

		// Im Offline-Modus keine Remote-DB-Verbindung versuchen
		if (initInOfflineMode) {
			this.remoteDb = null;
			// maybeInitialized prüft ob localDb bereit ist - wird durch den project()-Effect gesetzt
			maybeInitialized(true);
		} else {
			this._initRemoteDatabase(() => maybeInitialized(true));
		}

		// Track last project to detect actual changes
		// WICHTIG: undefined statt null, damit erste Prüfung mit projectId=null als Änderung erkannt wird
		let lastProjectId: string | null | undefined = undefined;
		let isInitializingLocalDb = false;

		effect(() => {
			const projectId = this.projectSignal();

			if (projectId === lastProjectId) return;
			if (isInitializingLocalDb) return;

			lastProjectId = projectId;
			isInitializingLocalDb = true;

			const initNewDb = () => {
				// localDbName liefert den DB-Namen (DataService: Projekt-ID, ProjectService: 'projects')
				const dbName = this.localDbName;
				if (dbName) {
					this._initLocalDatabase(dbName, () => {
						isInitializingLocalDb = false;
						maybeInitialized(!this.isInitialized);
					});
				} else {
					isInitializingLocalDb = false;
					this.localDb = null;
					maybeInitialized(!this.isInitialized);
				}
			};

			// Alte DB schließen bevor neue geöffnet wird
			// WICHTIG: isInitialized zurücksetzen damit waitForInitialization()
			// korrekt auf die neue DB wartet
			if (this.localDb) {
				this.isInitialized = false;
				// Referenz speichern und sofort auf null setzen,
				// damit db() während des Schließens null zurückgibt
				// und keine Race-Condition mit der schließenden DB entsteht
				const closingDb = this.localDb;
				this.localDb = null;
				this.onDatabasesClosed();
				closingDb.close().then(initNewDb).catch(() => initNewDb());
			} else {
				initNewDb();
			}
		});

		// Bei Offline-Modus-Wechsel (Benutzerpräferenz) reagieren
		// WICHTIG: Nur reagieren wenn sich der Modus TATSÄCHLICH ändert
		let previousUserOfflineMode = this.userOfflineModeSignal();
		effect(() => {
			const userOffline = this.userOfflineModeSignal();
			// Nur bei echtem Wechsel reagieren
			if (userOffline !== previousUserOfflineMode) {
				previousUserOfflineMode = userOffline;
				// Subklassen können auf Modus-Wechsel reagieren
				// (z.B. currentDb aktualisieren)
			}
		});

		// Auf Netzwerkänderungen reagieren - NUR wenn NICHT im userOfflineMode
		// Im userOfflineMode nutzt die App die lokale DB, Netzwerkänderungen sind irrelevant
		let previousNetworkOnline = this.networkOnlineSignal();
		effect(() => {
			const networkOnline = this.networkOnlineSignal();
			const userOffline = this.userOfflineModeSignal();

			// Im userOfflineMode: Netzwerkänderungen ignorieren, lokale DB wird verwendet
			if (userOffline) {
				previousNetworkOnline = networkOnline;
				return;
			}

			// Netzwerk von offline -> online (und nicht im userOfflineMode): Remote-DB reinitialisieren
			if (networkOnline && !previousNetworkOnline && !this.remoteDb) {
				this._initRemoteDatabase(() => {
					if (this.remoteDb) {
						this.onConnectionSuccess('Verbindung wiederhergestellt');
					}
				});
			}
			previousNetworkOnline = networkOnline;
		});
	}

	/**
	 * Gibt die aktuell zu verwendende DB zurück.
	 * Live-Sync: immer localDb (local-first).
	 * Checkout/Checkin: localDb im Offline-Modus, remoteDb im Online-Modus.
	 */
	protected db(): PouchDB.Database | null | undefined {
		if (this.isLiveSync()) return this.localDb;
		return this.userOfflineModeSignal() ? this.localDb : this.remoteDb;
	}

	// ========== Live-Sync ==========

	/**
	 * Initialisiert Datenbanken im Live-Sync-Modus.
	 * LocalDb ist immer die primäre Datenbank.
	 * Continuous Replication startet sobald remoteDb verfügbar ist.
	 */
	private _initDatabasesLiveSync(): void {
		let localDbReady = false;

		const maybeStartSync = () => {
			if (localDbReady && this.remoteDb) {
				this._startLiveSync();
			}
			if (localDbReady && !this.isInitialized) {
				this.isInitialized = true;
				this.onDatabasesInitialized();
			}
		};

		// LocalDb initialisieren (primäre DB)
		const dbName = this.localDbName;
		if (dbName) {
			this._initLocalDatabase(dbName, () => {
				if (this.localDb) {
					localDbReady = true;
					maybeStartSync();
				}
			});
		}

		// RemoteDb initialisieren (optional, für Replikation)
		if (this.networkOnlineSignal()) {
			this._initRemoteDatabase(() => {
				maybeStartSync();
			});
		}

		// Projektwechsel: LocalDb wechseln, Live-Sync neu starten
		// WICHTIG: Mit aktuellem Wert initialisieren, da _initLocalDatabase() oben bereits aufgerufen wurde.
		// Bei undefined würde der Effect sofort feuern und eine zweite _initLocalDatabase() auslösen → 409 Conflict.
		let lastProjectId: string | null | undefined = this.projectSignal();
		effect(() => {
			const projectId = this.projectSignal();
			if (projectId === lastProjectId) return;
			lastProjectId = projectId;

			this._stopLiveSync();
			const newDbName = this.localDbName;

			if (this.localDb) {
				this.isInitialized = false;
				const closingDb = this.localDb;
				this.localDb = null;
				this.onDatabasesClosed();
				closingDb.close()
					.then(() => this._reinitLocalForLiveSync(newDbName))
					.catch(() => this._reinitLocalForLiveSync(newDbName));
			} else {
				this._reinitLocalForLiveSync(newDbName);
			}
		});

		// Netzwerkänderungen: RemoteDb verbinden und Live-Sync starten
		let prevNetworkOnline = this.networkOnlineSignal();
		effect(() => {
			const networkOnline = this.networkOnlineSignal();
			if (networkOnline && !prevNetworkOnline && !this.remoteDb) {
				this._initRemoteDatabase(() => {
					if (this.remoteDb && this.localDb) {
						this._startLiveSync();
					}
				});
			}
			prevNetworkOnline = networkOnline;
		});
	}

	private _reinitLocalForLiveSync(dbName: string): void {
		if (!dbName) {
			this.localDb = null;
			return;
		}
		this._initLocalDatabase(dbName, () => {
			if (this.localDb) {
				this.isInitialized = true;
				this.onDatabasesInitialized();
				if (this.remoteDb) {
					this._startLiveSync();
				}
			}
		});
	}

	/**
	 * Hook für Subklassen, um Sync-Optionen zu überschreiben.
	 * Z.B. für Filtered Replication mit selector oder filter.
	 */
	protected getLiveSyncOptions(): PouchDB.Replication.SyncOptions {
		return {};
	}

	/**
	 * Stoppt und startet den Live-Sync neu (z.B. wenn sich Filter ändern).
	 */
	restartLiveSync(): void {
		this._stopLiveSync();
		this._startLiveSync();
	}

	/**
	 * Startet kontinuierliche bidirektionale Replikation zwischen localDb und remoteDb.
	 */
	protected _startLiveSync(): void {
		if (this._liveSyncHandle) return;
		if (!this.localDb || !this.remoteDb) return;

		this._liveSyncHandle = this.localDb.sync(this.remoteDb, {
			live: true,
			retry: true,
			batch_size: 10,
			...this.getLiveSyncOptions(),
			back_off_function: (delay: number) => {
				if (delay === 0) return 1000;
				return Math.min(delay * 2, 60000);
			}
		});

		this._liveSyncHandle
			.on('change', (info: any) => {
				this.liveSyncStatus.set('active');
				this.onLiveSyncChange(info);
			})
			.on('paused', (err: any) => {
				this.liveSyncStatus.set(err ? 'paused' : 'active');
				this.onLiveSyncPaused(err);
			})
			.on('active', () => {
				this.liveSyncStatus.set('active');
				this.onLiveSyncActive();
			})
			.on('denied', (err: any) => {
				console.error('[LiveSync] denied:', err);
			})
			.on('error', (err: any) => {
				console.error('[LiveSync] error:', err);
				this.liveSyncStatus.set('error');
				this.onLiveSyncError(err);
				this._liveSyncHandle = null;
			})
			.on('complete', () => {
				this.liveSyncStatus.set('idle');
				this._liveSyncHandle = null;
			});

		this.liveSyncStatus.set('active');
	}

	/**
	 * Stoppt die kontinuierliche Replikation.
	 */
	protected _stopLiveSync(): void {
		if (this._liveSyncHandle) {
			this._liveSyncHandle.cancel();
			this._liveSyncHandle = null;
		}
		this.liveSyncStatus.set('idle');
	}

	// Live-Sync Event-Hooks (Subklassen können überschreiben)
	protected onLiveSyncChange(info: any): void {}
	protected onLiveSyncPaused(err: any): void {}
	protected onLiveSyncActive(): void {}
	protected onLiveSyncError(err: any): void {}

	/**
	 * Verwirft die lokale Datenbank.
	 */
	protected async discardLocal(): Promise<void> {
		if (!this.localDb) return;

		await this.localDb.destroy();
		this.localDb = undefined;
	}
}
